import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "../lib/apiClient";
import { agentPortalMeta } from "../lib/divisions";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Search, ChevronLeft, ChevronRight, Phone } from "lucide-react";
import { formatDate } from "../lib/dates";

const PAGE_SIZE = 20;

export default function AgentPortal() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(() => Math.max(1, Number(searchParams.get("page")) || 1));
  const [updatedMap, setUpdatedMap] = useState({});
  const navigate = useNavigate();
  const { division = "ramp" } = useParams();

  const load = useCallback(() => {
    api.get("/agents", { params: { q, division } }).then((r) => setRows(r.data));
  }, [q, division]);

  useEffect(() => {
    api.get("/agents/meta").then((r) => setUpdatedMap(r.data.updated || {})).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  // Persist page in the URL so browser Back returns to the same page (not page 1).
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (page > 1) sp.set("page", String(page)); else sp.delete("page");
    setSearchParams(sp, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Reset to page 1 on filter/division change (honor URL page on first mount; StrictMode-safe).
  const filterSig = `${division}|${q}`;
  const prevSig = useRef(filterSig);
  useEffect(() => {
    if (prevSig.current === filterSig) return;
    prevSig.current = filterSig;
    setPage(1);
  }, [filterSig]);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const paged = useMemo(() => rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [rows, page]);

  // Clamp page if fewer pages than current (skip while data still loading).
  useEffect(() => {
    if (rows.length === 0) return;
    if (page > totalPages) setPage(totalPages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px]">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">{agentPortalMeta(division).eyebrow}</p>
        <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight">{agentPortalMeta(division).title}</h1>
        <p data-testid="roster-updated-note" className="text-xs text-muted-foreground mt-1.5">
          Seniority roster last updated {updatedMap[division] ? formatDate(updatedMap[division]) : "Aug 4, 2026"}
        </p>
      </div>

      <div className="relative mb-4 max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input data-testid="agent-search" placeholder="Search by name, EID or phone…" value={q}
          onChange={(e) => setQ(e.target.value)} className="rounded-sm pl-9 h-10" />
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/60 hover:bg-secondary/60">
              {["Rank", "EID", "Agent", "Type", "Phone", "WN SLI Seniority", "Hire Date"].map((h) => (
                <TableHead key={h} className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {paged.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground text-sm">No agents found.</TableCell></TableRow>
            )}
            {paged.map((a) => (
              <TableRow key={a.id} data-testid={`agent-row-${a.eid}`}
                onClick={() => navigate(`/grievants/${encodeURIComponent(a.name)}?division=${division}&eid=${encodeURIComponent(a.eid || "")}`)}
                className="cursor-pointer border-border">
                <TableCell className="font-mono text-xs tabular-nums">{a.rank}</TableCell>
                <TableCell className="font-mono text-xs">{a.eid}</TableCell>
                <TableCell className="text-sm font-medium">{a.name}</TableCell>
                <TableCell>
                  <span className={`text-xs px-2 py-0.5 rounded-sm font-medium ${a.employment === "FT" ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"}`}>{a.employment}</span>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{a.phone || "—"}</TableCell>
                <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{formatDate(a.sli_seniority_date)}</TableCell>
                <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{formatDate(a.hire_date)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between mt-3">
        <p className="text-xs text-muted-foreground">{rows.length.toLocaleString()} agent(s)</p>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="rounded-sm h-8" data-testid="agent-prev-page"
            disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-xs text-muted-foreground tabular-nums">Page {page} / {totalPages}</span>
          <Button variant="outline" size="sm" className="rounded-sm h-8" data-testid="agent-next-page"
            disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
